// W35.1 (2026-10-20): one projection behind the two subscription cards
// that sit OUTSIDE the Subscriptions tab:
//
//   History         third snapshot card, labelled NEXT SUBSCRIPTION
//                   (READ: launch-ready/approved-stack-2026-10-12/
//                   10-History-Consistency-Review-v5.html line 378:
//                   <small>NEXT SUBSCRIPTION</small>)
//   Colic Coverage  "Related subscription" aside card
//
// Both read GET /v1/contacts/:id/subscription-summary through the W35
// hook (screens/_shared/useSubscriptionSummary.ts; MEASURED bodies in its
// header) and format with the same helpers the Subscriptions tab uses
// (SubscriptionsReview/subscriptionState.ts), so the three surfaces can
// never disagree about a product, cadence, date or payment method.
//
// W40 (2026-10-21): lifted from screens/HistoryReview to screens/_shared
// (W35.1 flagged the cross-screen import from ColicCoverageReview).
//
// Nothing here invents a value: a missing field renders as "not on file"
// and the sink's `status: 'none'` renders the honest empty. A failed read
// renders the error code, never fixture data.

import {
  hasSubscriptionOnFile,
  type ContactReadResult,
  type SubscriptionPayment,
  type SubscriptionSummary,
} from './useSubscriptionSummary.js';
import {
  describeCadence,
  describePaymentType,
  describePaymentValidity,
  describeSubscriptionStatus,
  describeSummaryDate,
} from '../SubscriptionsReview/subscriptionState.js';

/** Same loading label the Subscriptions tab passes to its DataBoundary. */
export const SUBSCRIPTION_CARD_LOADING = 'Loading subscription…';
/** Same error title the Subscriptions tab passes to its DataBoundary. */
export const SUBSCRIPTION_CARD_ERROR_TITLE = 'Subscription summary could not load';
/** The honest empty for `status: 'none'`. */
export const SUBSCRIPTION_CARD_NONE = 'No active subscription on file';
/** Fallback error code, identical to the Subscriptions tab's. */
export const SUBSCRIPTION_CARD_ERROR_FALLBACK_CODE = 'SUBSCRIPTION_SUMMARY_UNAVAILABLE';

export interface SubscriptionCardFacts {
  /** 'Osteo-MAX + Gut X', or 'Product not on file'. */
  product: string;
  /** 'OMGX200', or null when HubSpot has no SKU. */
  sku: string | null;
  /** 'Every 90 days' (describeCadence). */
  cadence: string;
  /** 'January 18, 2027' (describeSummaryDate), or 'Not on file'. */
  nextCharge: string;
  /** 'Shop Pay ending in 9306' (describeSubscriptionPayment). */
  payment: string;
  /** 'Active' (describeSubscriptionStatus). */
  status: string;
}

/**
 * 'Shop Pay ending in 9306'. A last4 that is not on file leaves just the
 * type label; a payment method HubSpot flags invalid appends the same
 * attention label the Subscriptions tab shows.
 */
export function describeSubscriptionPayment(payment: SubscriptionPayment): string {
  const type = describePaymentType(payment.type);
  const last4 = payment.last4 ? ` ending in ${payment.last4}` : '';
  const attention = payment.valid === false ? ` · ${describePaymentValidity(false)}` : '';
  return `${type}${last4}${attention}`;
}

/** Pure projection of a summary that is on file. Exported for tests. */
export function subscriptionCardFacts(summary: SubscriptionSummary): SubscriptionCardFacts {
  return {
    product: summary.productLabel ?? 'Product not on file',
    sku: summary.productSku,
    cadence: describeCadence(summary.frequencyDays, summary.frequencyLabel),
    nextCharge: describeSummaryDate(summary.nextChargeDate),
    payment: describeSubscriptionPayment(summary.payment),
    status: describeSubscriptionStatus(summary.status, summary.subscriptionStatusLabel),
  };
}

export type SubscriptionCardState =
  /** Preview (?preview=) or dev-mock session: the hook did not fetch. */
  | { kind: 'skipped' }
  | { kind: 'loading' }
  | { kind: 'error'; errorCode: string; httpStatus: number | null; detail: string | null }
  /** The sink's `status: 'none'`. */
  | { kind: 'none' }
  | { kind: 'on-file'; facts: SubscriptionCardFacts };

/** Maps the hook result to what a card renders. Exported for tests. */
export function subscriptionCardState(read: ContactReadResult<SubscriptionSummary>): SubscriptionCardState {
  if (read.status === 'skipped') return { kind: 'skipped' };
  if (read.status === 'loading') return { kind: 'loading' };
  if (read.status === 'error' || !read.data) {
    return {
      kind: 'error',
      errorCode: read.errorCode ?? SUBSCRIPTION_CARD_ERROR_FALLBACK_CODE,
      httpStatus: read.httpStatus ?? null,
      detail: read.errorDetail ?? null,
    };
  }
  if (!hasSubscriptionOnFile(read.data)) return { kind: 'none' };
  return { kind: 'on-file', facts: subscriptionCardFacts(read.data) };
}

/** 'ROUTE_DEPENDENCY_NOT_WIRED (HTTP 503)' / 'FETCH_FAILED'. */
export function describeSubscriptionCardError(state: Extract<SubscriptionCardState, { kind: 'error' }>): string {
  return state.httpStatus ? `${state.errorCode} (HTTP ${state.httpStatus})` : state.errorCode;
}
